import React from 'react';

import DOMPurify from 'dompurify';

import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Alert, Box, IconButton, Link, Paper, Stack, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';

import { buildPieModel } from '~/pages/LeLens/cardView';
import LensEChart from '~/pages/LeLens/cards/LensEChart';
import {
  buildChartModel,
  formatLensValue,
  inferLensFieldFormat,
  isNumeric,
  prepareLensTimeAxis,
  seriesColors,
} from '~/pages/LeLens/lensChart';
import {
  type LensEChartsOption,
  type LensEChartsTheme,
  buildLensEChartsOption,
  buildLensEChartsPieOption,
  buildLensEChartsScatterOption,
} from '~/pages/LeLens/lensEChartsOption';
import { valueLabel } from '~/pages/LeLens/lensTime';

import type { LeDataContentBlock } from '~/types/leData';
import type { Lens, LensCardConfig, LensCardType, LensValueFormat } from '~/types/leLens';

import { downloadCsvFile } from '~/utils/csvDownload';
import { getLeDataApiErrorMessage } from '~/utils/leData/getLeDataApiErrorMessage';
import { inlineSqlReferences } from '~/utils/leData/inlineSqlReferences';
import { stripMarkdownTables } from '~/utils/leData/stripMarkdownTables';
import { isLikelyRowCapped, normalizeTableBlock } from '~/utils/leData/tableBlock';

import LeDataMarkdownPreview from './LeDataMarkdownPreview';
import SqlCodeMirrorBlock from './SqlCodeMirrorBlock';
import {
  type FencedCodeSegment,
  extractFencedCodeSegments,
  restoreFencedCodeMarkdown,
} from './extractFencedCodeSegments';
import {
  ELEVATED_SURFACE_DARK,
  ELEVATED_SURFACE_DARK_RULE,
  KPI_DELTA_DIRECTION,
  type KpiDeltaDirection,
  type KpiDeltaSentiment,
  MAX_KPI_TILES,
  deriveKpiDeltaDirection,
  isEmptyChart,
  isKpiBlockKind,
  kpiDeltaColorRole,
  normalizeKpiSentiment,
} from './uiTokens';
import { useSmoothReveal } from './useSmoothReveal';

const BLOCK_ID_FILENAME_LENGTH = 8;

/** Above this row count the grid stops growing (autoHeight) and scrolls inside a bounded, sticky-header frame. */
const LARGE_TABLE_ROW_THRESHOLD = 12;
/** Bounded height for large tables; the header stays pinned and rows scroll within this frame. */
const TABLE_SCROLL_HEIGHT = 420;
/** Non-dismissable footer notice shown when the result set appears cut off by the server row cap. */
const ROW_CAP_CAPTION = 'Showing first 1,000 rows (server limit). Totals and charts reflect only these rows.';

const assistantMarkdownSx = {
  fontSize: '0.875rem',
  lineHeight: 1.5,
  '& > :first-child, & > :first-child > :first-child': {
    mt: 0,
  },
  '& > :last-child, & > :last-child > :last-child': {
    mb: 0,
  },
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    fontSize: '1rem',
    lineHeight: 1.4,
    fontWeight: 600,
    mt: 1.5,
    mb: 1,
  },
  '& p': {
    mb: 1,
  },
};

function pickString(payload: Record<string, unknown>, keys: Array<string>): string {
  for (const key of keys) {
    const v = payload[key];
    if (typeof v === 'string' && v.trim()) {
      return v;
    }
  }
  return '';
}

function pickStringArray(payload: Record<string, unknown>, keys: Array<string>): Array<string> {
  for (const key of keys) {
    const value = payload[key];
    if (!Array.isArray(value)) {
      continue;
    }
    const items = value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
    if (items.length > 0) {
      return items;
    }
  }
  return [];
}

/**
 * Business abbreviations and currency codes that must render all-caps in labels. Allowlisted so a key
 * like `ttv_aud` reads as "TTV AUD"; deliberately NOT a blanket "uppercase short words" rule, which
 * would wreck ordinary words ("per day", "top 20"). Compared case-insensitively.
 */
const LABEL_ACRONYMS = new Set([
  // Metrics and business abbreviations
  'ttv',
  'aov',
  'ltv',
  'cac',
  'roas',
  'gmv',
  'gsv',
  'mpv',
  'rpv',
  'mpi',
  'br',
  'ar',
  'lpp',
  'ltlp',
  'lpc',
  'spp',
  'gha',
  'cug',
  'vip',
  'kpi',
  'roi',
  // Technical abbreviations
  'id',
  'url',
  'sql',
  'api',
  'ai',
  // Currency codes
  'usd',
  'aud',
  'gbp',
  'eur',
  'nzd',
  'sgd',
  'hkd',
  'cad',
  'jpy',
  'myr',
  'thb',
  'idr',
]);

/**
 * Turns a raw series/axis key into a display label: separators become spaces, words are title-cased,
 * and any word that is a known acronym or currency code (see {@link LABEL_ACRONYMS}) is uppercased in
 * full, so "ttv_aud" reads as "TTV AUD" rather than "Ttv Aud".
 */
export function formatChartLabel(raw: string): string {
  return raw
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((word) => {
      if (!word) {
        return word;
      }
      if (LABEL_ACRONYMS.has(word.toLowerCase())) {
        return word.toUpperCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
}

/**
 * Most x-axis ticks to label before thinning kicks in. Charts fill the container width, so with many
 * points the labels would collide; past this count we show roughly this many, evenly spaced.
 */
const X_AXIS_MAX_VISIBLE_TICKS = 12;
/** Standard plot height for axis-based charts; the empty state matches it so layout never jumps. */
const CHART_HEIGHT = 280;
/** Pie/donut charts get a little extra height for the outside labels. */
const PIE_CHART_HEIGHT = 320;
const PIE_OUTER_RADIUS = 100;
/** Donut hole: 60% of the outer radius, per the chart anatomy standard. */
const PIE_INNER_RADIUS = PIE_OUTER_RADIUS * 0.6;
const CHART_EMPTY_MESSAGE = 'No data for this view.';
/** Internal numeric field used when an ISO-like x-axis needs chronological spacing. */
const TIME_AXIS_KEY = '__chartTimestamp';

function isDateLikeLabel(value: unknown): boolean {
  if (typeof value !== 'string') {
    return false;
  }
  const normalized = value.trim();
  return /^\d{4}-\d{2}-\d{2}(?:[T\s].*)?$/.test(normalized) || /^\d{4}-\d{2}$/.test(normalized);
}

/**
 * Date strings are categories to Recharts unless the axis is explicitly numeric. Convert a wholly
 * date-like x column to epoch milliseconds so points are placed by elapsed time, not row order.
 */
export function prepareTimeAxisSeries(
  series: Array<Record<string, unknown>>,
  xKey: string,
): { data: Array<Record<string, unknown>>; dataKey: string; isTimeAxis: boolean } {
  if (!series.every((entry) => isDateLikeLabel(entry[xKey]))) {
    return { data: series, dataKey: xKey, isTimeAxis: false };
  }
  const timestamps = series.map((entry) => (typeof entry[xKey] === 'string' ? Date.parse(entry[xKey]) : NaN));
  const isTimeAxis = timestamps.length > 0 && timestamps.every(Number.isFinite);

  if (!isTimeAxis) {
    return { data: series, dataKey: xKey, isTimeAxis: false };
  }

  return {
    data: series.map((entry, index) => ({ ...entry, [TIME_AXIS_KEY]: timestamps[index] })),
    dataKey: TIME_AXIS_KEY,
    isTimeAxis: true,
  };
}

/** Keep time ticks compact and unambiguous while retaining UTC, as in BigQuery TIMESTAMP values. */
/** The largest instant a JavaScript Date can represent. */
const MAX_EPOCH_MS = 8.64e15;

export function formatTimeAxisTick(value: unknown): string {
  // Finite is not the same as a date. A seconds-for-milliseconds mixup lands
  // outside the range Date can hold, and formatting it throws a RangeError out
  // of the tick formatter, taking the chart down mid-render rather than
  // degrading to a label.
  if (typeof value !== 'number' || !Number.isFinite(value) || Math.abs(value) > MAX_EPOCH_MS) {
    return String(value ?? '');
  }
  return valueLabel(new Date(value)) ?? String(value);
}

/**
 * Recharts `interval` for the x axis reads as "skip N ticks between labels": 0 labels every category.
 * Charts fill the container width rather than scrolling, so a wide series would crowd its labels. We
 * label every point up to {@link X_AXIS_MAX_VISIBLE_TICKS}, then thin evenly so roughly that many
 * remain. Points, lines, and bars are unaffected: only which ticks are labelled changes.
 */
export function computeTickInterval(pointCount: number): number {
  // A non-finite or non-positive count has nothing to thin; guard first so we never return a negative
  // interval (which Recharts would reject).
  if (!Number.isFinite(pointCount) || pointCount <= 0) {
    return 0;
  }
  if (pointCount <= X_AXIS_MAX_VISIBLE_TICKS) {
    return 0;
  }
  return Math.ceil(pointCount / X_AXIS_MAX_VISIBLE_TICKS) - 1;
}

function createValueFormatter(
  payload: Record<string, unknown>,
  key: string,
  values: ReadonlyArray<unknown> = [],
): ((value: unknown) => string) | undefined {
  const formatMap =
    payload.valueFormats && typeof payload.valueFormats === 'object' && !Array.isArray(payload.valueFormats)
      ? (payload.valueFormats as Record<string, unknown>)
      : null;
  const formatFromMap = formatMap?.[key];
  const formatFromKey = payload[`${key}Format`];
  const raw =
    (typeof formatFromMap === 'string' ? formatFromMap : null) ||
    (typeof formatFromKey === 'string' ? formatFromKey : null) ||
    (typeof payload.valueFormat === 'string' ? payload.valueFormat : null);

  const format = raw?.toLowerCase();
  const currency = typeof payload.currencyCode === 'string' && payload.currencyCode ? payload.currencyCode : 'AUD';

  if (!format) {
    const inferred = inferLensFieldFormat(key, values);
    // Numeric cells should never print the raw implementation precision that
    // BigQuery floating point can carry. This preserves strings and labels.
    return (value: unknown): string => {
      const numeric = finiteNumericValue(value);
      if (numeric !== undefined) {
        return formatLensValue(numeric, inferred);
      }
      return typeof value === 'string' ? value : '';
    };
  }

  const numberFormatter = new Intl.NumberFormat(undefined, {
    maximumFractionDigits: format === 'integer' ? 0 : 2,
  });
  const compactFormatter = new Intl.NumberFormat(undefined, {
    notation: 'compact',
    maximumFractionDigits: 1,
  });
  const currencyFormatter = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  });
  const percentFormatter = new Intl.NumberFormat(undefined, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  return (value: unknown): string => {
    const numeric = finiteNumericValue(value);
    if (numeric === undefined) {
      return typeof value === 'string' ? value : '';
    }
    switch (format) {
      case 'integer':
        return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(numeric);
      case 'number':
        return numberFormatter.format(numeric);
      case 'compact':
        return compactFormatter.format(numeric);
      case 'currency':
        return currencyFormatter.format(numeric);
      case 'percent':
        return percentFormatter.format(numeric > 1 ? numeric / 100 : numeric);
      default:
        return String(numeric);
    }
  };
}

function createAxisTickFormatter(
  payload: Record<string, unknown>,
  key: string,
): ((value: unknown) => string) | undefined {
  const formatMap =
    payload.valueFormats && typeof payload.valueFormats === 'object' && !Array.isArray(payload.valueFormats)
      ? (payload.valueFormats as Record<string, unknown>)
      : null;
  const formatFromMap = formatMap?.[key];
  const formatFromKey = payload[`${key}Format`];
  const raw =
    (typeof formatFromMap === 'string' ? formatFromMap : null) ||
    (typeof formatFromKey === 'string' ? formatFromKey : null) ||
    (typeof payload.valueFormat === 'string' ? payload.valueFormat : null);

  if (!raw) {
    return undefined;
  }

  const format = raw.toLowerCase();
  const currency = typeof payload.currencyCode === 'string' && payload.currencyCode ? payload.currencyCode : 'AUD';
  const integerFormatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });
  const compactFormatter = new Intl.NumberFormat(undefined, {
    notation: 'compact',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  const currencyFormatter = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    notation: 'compact',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  const percentFormatter = new Intl.NumberFormat(undefined, {
    style: 'percent',
    maximumFractionDigits: 0,
  });

  return (value: unknown): string => {
    if (typeof value !== 'number' || Number.isNaN(value)) {
      return String(value ?? '');
    }

    switch (format) {
      case 'currency':
        return currencyFormatter.format(value);
      case 'compact':
        return compactFormatter.format(value);
      case 'percent':
        return percentFormatter.format(value > 1 ? value / 100 : value);
      case 'integer':
      case 'number':
        return integerFormatter.format(value);
      default:
        return String(Math.round(value));
    }
  };
}

function renderChartContainer(payload: Record<string, unknown>, content: React.ReactNode): React.ReactNode {
  const title = pickString(payload, ['title']);
  const subtitle = pickString(payload, ['subtitle', 'description']);

  return (
    <Box>
      {title ? (
        <Typography
          sx={{ fontSize: '14px', fontWeight: 600, color: 'text.primary', textAlign: 'left', mb: subtitle ? 0.25 : 1 }}
        >
          {title}
        </Typography>
      ) : null}
      {subtitle ? (
        <Typography sx={{ fontSize: '12px', fontWeight: 450, color: 'text.secondary', textAlign: 'left', mb: 1 }}>
          {subtitle}
        </Typography>
      ) : null}
      {content}
    </Box>
  );
}

function normalizePostChartMarkdown(raw: string): string {
  const normalized = raw.replace(/\r\n/g, '\n').trim();
  const lines = normalized
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length < 3) {
    return raw;
  }

  const hasExistingMarkdown = lines.some((line) => /^(#{1,6}\s|[-*+]\s|\d+\.\s|```|>|\|)/.test(line));
  if (hasExistingMarkdown) {
    return raw;
  }

  const [firstLine, ...rest] = lines;
  const looksLikeHeading = firstLine.length <= 80 && !/[.!?:]$/.test(firstLine);
  if (!looksLikeHeading) {
    return raw;
  }

  return `### ${firstLine}\n\n${rest.map((line) => `- ${line}`).join('\n')}`;
}

function isSqlFenceLanguage(language: string): boolean {
  return language === 'sql' || language === 'bigquery' || language === 'bq';
}

function parseChartFence(segment: FencedCodeSegment): Record<string, unknown> | null {
  if (segment.language !== 'chart') {
    return null;
  }

  try {
    const parsed = JSON.parse(segment.content.trim()) as unknown;
    if (
      parsed &&
      typeof parsed === 'object' &&
      !Array.isArray(parsed) &&
      Array.isArray((parsed as Record<string, unknown>).data)
    ) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    // Malformed chart fences fall through to the markdown code-block renderer.
  }

  return null;
}

/** Maps askRichard's renderer-specific chart labels to the matching Lens card type. */
export function lensCardTypeForChart(payload: Record<string, unknown>): LensCardType {
  const raw =
    pickString(payload, ['chartType', 'chart', 'variant', 'kind', 'subtype']).toLowerCase() ||
    (typeof payload.type === 'string' ? payload.type.toLowerCase() : '');

  switch (raw) {
    case 'line':
    case 'multiline':
    case 'multi-line':
    case 'multi_line':
      return 'line';
    case 'stackedbar':
    case 'stacked-bar':
    case 'stacked_bar':
      return 'stacked';
    case 'area':
      return 'area';
    case 'pie':
    case 'pizza':
      return 'pie';
    case 'combo':
    case 'composed':
      return 'combo';
    case 'scatter':
      return 'scatter';
    default:
      return 'bar';
  }
}

function renderCodeSegment(
  segment: FencedCodeSegment,
  theme: Theme,
  onSavedLens?: (lens: Lens) => void,
): React.ReactNode {
  const chartPayload = parseChartFence(segment);
  if (chartPayload) {
    return renderChartBlock(chartPayload, theme);
  }

  if (isSqlFenceLanguage(segment.language)) {
    return <SqlCodeMirrorBlock onSavedLens={onSavedLens} value={segment.content.trim()} />;
  }

  return <LeDataMarkdownPreview content={restoreFencedCodeMarkdown(segment)} />;
}

/**
 * A defensive reader-surface fallback for malformed or mixed-length Markdown
 * fences. The structured extractor still owns evidence discovery; this only
 * ensures implementation payloads never become visible prose in a Lens answer.
 */
function stripFencedCodeFromAnswer(raw: string): string {
  return raw.replace(/(?:^|\n)(?:`{3,}|~{3,})[^\n]*\n[\s\S]*?(?:\n(?:`{3,}|~{3,})[^\n]*|$)/g, '\n');
}

function renderTextBlock(
  markdown: string,
  theme: Theme,
  blockId: string,
  stripTables = false,
  duplicateSql = '',
  stripCodeFences = false,
  stripChartFences = false,
  onSavedLens?: (lens: Lens) => void,
  streaming = false,
): React.ReactNode {
  const picked = inlineSqlReferences(markdown);
  // When the answer also draws a chart, the inline markdown table duplicates the chart's numbers
  // (the raw rows still live in the evidence drawer's Data tab), so drop it to avoid a table then
  // chart flash. Prose, headings, lists and code fences are untouched.
  const tableFree = stripTables ? stripMarkdownTables(picked) : picked;
  const raw = stripCodeFences ? stripFencedCodeFromAnswer(tableFree) : tableFree;
  if (!raw) {
    if (streaming) {
      return null;
    }
    // A parenthetical developer note is not an answer. Say what happened in a sentence a reader can
    // act on, because an empty block here means the question needs asking a different way.
    return (
      <Typography variant="body2" color="text.secondary">
        askRichard returned nothing for this part of the answer. Ask the question again.
      </Typography>
    );
  }

  const segments = extractFencedCodeSegments(raw);
  // Lens answers can carry the same query as a prose fence and as governed
  // evidence. The answer leads with the result; the evidence shelf owns SQL.
  const visibleSegments = segments.filter((segment) => {
    if (segment.kind !== 'code') {
      return true;
    }
    if (stripChartFences && segment.language === 'chart') {
      return false;
    }
    // Lens answers lead with the business decision and rendered proposal. The
    // working shelf owns every implementation fence, including chart JSON.
    if (stripCodeFences) {
      return false;
    }
    return !isSqlFenceLanguage(segment.language) || !duplicateSql || segment.content.trim() !== duplicateSql.trim();
  });
  if (visibleSegments.length === 0 || !visibleSegments.some((segment) => segment.content.trim())) {
    return null;
  }
  if (visibleSegments.some((segment) => segment.kind === 'code')) {
    const hasValidChartFence = visibleSegments.some(
      (segment) => segment.kind === 'code' && parseChartFence(segment) !== null,
    );
    return (
      <Box sx={{ '& pre': { overflow: 'auto' } }}>
        {visibleSegments.map((segment, index) => {
          const marginBottom = index === visibleSegments.length - 1 ? 0 : 1.5;

          // The data-le-data-chart-id attribute wraps only the chart itself (not any sibling
          // control), so an exported answer PDF screenshots just the chart at this id.
          return segment.kind === 'code' ? (
            <Box
              key={`code-${index}`}
              data-le-data-chart-id={segment.language === 'chart' ? `${blockId}-chart-${index}` : undefined}
              sx={{ mb: marginBottom }}
            >
              {renderCodeSegment(segment, theme, onSavedLens)}
            </Box>
          ) : (
            <Box key={`markdown-${index}`} sx={{ mb: index === visibleSegments.length - 1 ? 0 : 1.5 }}>
              <LeDataMarkdownPreview
                content={hasValidChartFence ? normalizePostChartMarkdown(segment.content) : segment.content}
                boxProps={{ sx: assistantMarkdownSx }}
                streaming={streaming}
              />
            </Box>
          );
        })}
      </Box>
    );
  }

  return (
    <Box sx={{ '& pre': { overflow: 'auto' } }}>
      <LeDataMarkdownPreview
        content={visibleSegments.map((segment) => segment.content).join('\n\n')}
        boxProps={{ sx: assistantMarkdownSx }}
        streaming={streaming}
      />
    </Box>
  );
}

function renderSqlBlock(
  payload: Record<string, unknown>,
  onKeepLive?: (sqlText: string) => void,
  prominentLensSave = false,
  onSavedLens?: (lens: Lens) => void,
): React.ReactNode {
  const sqlText = pickString(payload, ['sql', 'content', 'query', 'statement', 'body']);
  const dialectRaw = typeof payload.dialect === 'string' ? payload.dialect.trim() : '';
  const dialectLabel = dialectRaw.length > 0 ? dialectRaw[0].toUpperCase() + dialectRaw.slice(1).toLowerCase() : '';
  if (!sqlText) {
    return (
      <Typography variant="body2" color="text.secondary">
        (Empty SQL block)
      </Typography>
    );
  }
  return (
    <Box>
      {dialectLabel ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
          {dialectLabel}
        </Typography>
      ) : null}
      <SqlCodeMirrorBlock
        value={sqlText}
        onKeepLive={onKeepLive ? () => onKeepLive(sqlText) : undefined}
        onSavedLens={onSavedLens}
        prominentLensSave={prominentLensSave}
      />
    </Box>
  );
}

/** SQL result columns are uniformly typed, so a bounded sample is enough to classify a column. */
const COLUMN_TYPE_SAMPLE_SIZE = 100;
const NUMERIC_TEXT_RE = /^[-+]?(?:\d+\.?\d*|\.\d+)(?:e[-+]?\d+)?$/i;

/** BigQuery DECIMAL values arrive as strings in some result payloads. They are
 * still measures, not labels, when the entire cell is a finite numeric literal. */
function finiteNumericValue(value: unknown): number | undefined {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === 'string' && NUMERIC_TEXT_RE.test(value.trim())) {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : undefined;
  }
  return undefined;
}

/** A column is numeric only if every populated cell is a real number, so text columns stay left-aligned. */
function isNumericColumn(rows: Array<Record<string, unknown>>, field: string): boolean {
  let sawNumber = false;
  const limit = Math.min(rows.length, COLUMN_TYPE_SAMPLE_SIZE);
  for (let i = 0; i < limit; i += 1) {
    const value = rows[i][field];
    if (value === null || value === undefined || value === '') {
      continue;
    }
    if (finiteNumericValue(value) !== undefined) {
      sawNumber = true;
      continue;
    }
    return false;
  }
  return sawNumber;
}

/**
 * Builds a column definition with type-aware presentation: numeric columns are right-aligned with a
 * numeric sort comparator (via `type: 'number'`), and any column with a declared value format reuses
 * the existing currency/percent/integer/compact formatter for display. Sorting is left enabled.
 */
function buildColDef(
  field: string,
  headerName: string,
  rows: Array<Record<string, unknown>>,
  payload: Record<string, unknown>,
): GridColDef {
  const numeric = isNumericColumn(rows, field);
  const formatter = createValueFormatter(
    payload,
    field,
    rows.map((row) => row[field]),
  );
  return {
    field,
    headerName,
    flex: 1,
    minWidth: 120,
    ...(numeric ? { type: 'number' as const, headerAlign: 'right' as const, align: 'right' as const } : {}),
    ...(formatter ? { valueFormatter: (value: unknown) => formatter(value) } : {}),
  };
}

function rowsToDataGrid(
  rows: Array<Record<string, unknown>>,
  columns: unknown,
  payload: Record<string, unknown>,
): { rowModels: Array<Record<string, unknown>>; colDefs: Array<GridColDef> } {
  const rowModels = rows.map((row, i) => ({ ...row, id: i }));

  if (Array.isArray(columns) && columns.length > 0 && typeof columns[0] === 'string') {
    const headers = columns as Array<string>;
    const colDefs: Array<GridColDef> = headers.map((field) => buildColDef(field, field, rows, payload));
    return { rowModels, colDefs };
  }

  if (Array.isArray(columns) && columns.length > 0 && typeof columns[0] === 'object' && columns[0] !== null) {
    const cols = columns as Array<{ field?: string; headerName?: string }>;
    const colDefs: Array<GridColDef> = cols
      .map((c) => {
        const field = c.field || c.headerName || '';
        if (!field) {
          return null;
        }
        return buildColDef(field, c.headerName || c.field || field, rows, payload);
      })
      .filter(Boolean);
    return { rowModels, colDefs };
  }

  const first = rows[0] ?? {};
  const headers = Object.keys(first);
  const colDefs: Array<GridColDef> = headers.map((field) => buildColDef(field, field, rows, payload));
  return { rowModels, colDefs };
}

function renderTableBlock(
  payload: Record<string, unknown>,
  blockId: string,
  hideDownload = false,
  actions?: React.ReactNode,
): React.ReactNode {
  const markdown = pickString(payload, ['markdown', 'md']);
  if (markdown) {
    return <LeDataMarkdownPreview content={markdown} />;
  }

  const html = pickString(payload, ['html']);
  if (html) {
    const safe = DOMPurify.sanitize(html);
    return <Box sx={{ overflow: 'auto' }} dangerouslySetInnerHTML={{ __html: safe }} />;
  }

  const table = normalizeTableBlock(payload);

  if (table.rowCount === 0) {
    // A real zero-row result is an answer, not a failure: the query ran and matched nothing. The
    // copy has to say that, because "(No table rows)" reads like the table failed to load.
    return (
      <Typography variant="body2" color="text.secondary">
        No rows matched this question. Try a wider period or fewer filters.
      </Typography>
    );
  }

  const { rowModels, colDefs } = rowsToDataGrid(table.dataRows, table.columnsForGrid, payload);
  const isLargeTable = rowModels.length > LARGE_TABLE_ROW_THRESHOLD;
  const rowCapped = isLikelyRowCapped(table.rowCount);

  const handleDownload = () => {
    const blockIdShort = blockId.slice(0, BLOCK_ID_FILENAME_LENGTH);
    /** Strip separators AND the T/Z marker for a purely-numeric, filesystem-clean stamp. */
    const isoTimestamp = new Date().toISOString().replace(/[-:.TZ]/g, '');
    const filename = `askrichard-${blockIdShort}-${isoTimestamp}.csv`;
    downloadCsvFile(table.dataRows, table.headers, filename);
  };

  return (
    <Stack spacing={0.5}>
      {actions || (table.canDownload && !hideDownload) ? (
        <Stack direction="row" alignItems="center" justifyContent="flex-end" flexWrap="wrap" gap={0.5}>
          {actions}
          {table.canDownload && !hideDownload ? (
            <IconButton size="small" title="Download CSV" aria-label="Download CSV" onClick={handleDownload}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          ) : null}
        </Stack>
      ) : null}
      <DataGrid
        rows={rowModels}
        columns={colDefs}
        // Small tables grow to fit; large ones scroll inside a bounded frame with the header pinned.
        autoHeight={!isLargeTable}
        disableRowSelectionOnClick
        density="compact"
        hideFooter={rowModels.length <= 10}
        pageSizeOptions={[10, 25, 50]}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
        sx={{
          minWidth: 360,
          border: 'none',
          ...(isLargeTable ? { height: TABLE_SCROLL_HEIGHT } : {}),
          '& .MuiDataGrid-cell': { fontSize: '0.8125rem', fontVariantNumeric: 'tabular-nums' },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontSize: '11px',
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: 'text.secondary',
          },
          '& .MuiDataGrid-row:hover': { backgroundColor: 'action.hover' },
        }}
      />
      {rowCapped ? (
        <Box sx={{ borderLeft: '2px solid', borderColor: 'warning.main', pl: 1, py: 0.25 }}>
          <Typography variant="caption" sx={{ display: 'block', fontSize: '12px', color: 'text.secondary' }}>
            {ROW_CAP_CAPTION}
          </Typography>
        </Box>
      ) : null}
    </Stack>
  );
}

/**
 * Empty chart: keep the title/subtitle (scope) and hold the plot's height so the surrounding
 * answer never reflows, then say plainly that there is nothing to plot.
 */
function renderEmptyChartState(payload: Record<string, unknown>, height = CHART_HEIGHT): React.ReactNode {
  return renderChartContainer(
    payload,
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height,
      }}
    >
      <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>{CHART_EMPTY_MESSAGE}</Typography>
    </Box>,
  );
}

/** The small theme slice the shared ECharts option builders need. */
function threadEChartsTheme(theme: Theme, config: LensCardConfig): LensEChartsTheme {
  const isDark = theme.palette.mode === 'dark';
  return {
    isDark,
    palette: seriesColors(isDark, config.colors),
    text: theme.palette.text.primary,
    textMuted: theme.palette.text.secondary,
    grid: theme.palette.divider,
    axis: theme.palette.divider,
    surface: theme.palette.background.paper,
    fontFamily: String(theme.typography.fontFamily ?? 'inherit'),
  };
}

function formatForChartField(
  payload: Record<string, unknown>,
  key: string,
  rows: Array<Record<string, unknown>>,
): LensValueFormat | undefined {
  const formatMap =
    payload.valueFormats && typeof payload.valueFormats === 'object' && !Array.isArray(payload.valueFormats)
      ? (payload.valueFormats as Record<string, unknown>)
      : null;
  const raw = formatMap?.[key] ?? payload[`${key}Format`] ?? payload.valueFormat;
  if (typeof raw !== 'string') {
    return inferLensFieldFormat(
      key,
      rows.map((row) => row[key]),
    );
  }

  switch (raw.toLowerCase()) {
    case 'integer':
      return { kind: 'number', decimals: 0 };
    case 'number':
    case 'compact':
      return { kind: 'number' };
    case 'currency':
      return { kind: 'currency', currency: typeof payload.currencyCode === 'string' ? payload.currencyCode : 'AUD' };
    case 'percent': {
      const values = rows.map((row) => Number(row[key])).filter(Number.isFinite);
      return { kind: 'percent', percentScale: values.some((value) => Math.abs(value) > 1) ? 'percent' : 'unit' };
    }
    default:
      return undefined;
  }
}

function chartColumns(rows: Array<Record<string, unknown>>): Array<string> {
  return [...new Set(rows.flatMap((row) => Object.keys(row)))];
}

function threadChartConfig(
  payload: Record<string, unknown>,
  rows: Array<Record<string, unknown>>,
  xKey: string,
  seriesKeys: Array<string>,
  type: LensCardType,
  marks?: Record<string, 'bar' | 'line' | 'area'>,
): LensCardConfig {
  const fields = [...new Set([xKey, ...seriesKeys])];
  const fieldFormats = Object.fromEntries(
    fields.flatMap((key) => {
      const format = formatForChartField(payload, key, rows);
      return format ? [[key, format]] : [];
    }),
  );
  const xAxisLabel = pickString(payload, ['xAxisLabel', 'xLabel']);
  const yAxisLabel = pickString(payload, ['yAxisLabel', 'yLabel']);
  const fieldLabels = Object.fromEntries(
    fields.map((key) => [
      key,
      key === xKey && xAxisLabel
        ? xAxisLabel
        : key === seriesKeys[0] && yAxisLabel
          ? yAxisLabel
          : formatChartLabel(key),
    ]),
  );

  return {
    xColumn: xKey,
    series: seriesKeys,
    stacked: type === 'stacked',
    seriesMarks: marks,
    fieldLabels,
    fieldFormats: Object.keys(fieldFormats).length ? fieldFormats : undefined,
  };
}

/** ECharts host shared with Lens, so thread answers and saved cards use one renderer. */
function renderThreadEChart(option: LensEChartsOption, height: number, label: string): React.ReactNode {
  return (
    <Box sx={{ width: '100%', height, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
      <LensEChart ariaLabel={label} height={height} option={option} />
    </Box>
  );
}

function renderChartBlock(payload: Record<string, unknown>, theme: Theme, preferredHeight?: number): React.ReactNode {
  const data = payload.data;
  const xKey =
    (typeof payload.xKey === 'string' && payload.xKey) ||
    (typeof payload.labelKey === 'string' && payload.labelKey) ||
    'name';
  const yKey =
    (typeof payload.yKey === 'string' && payload.yKey) ||
    (typeof payload.valueKey === 'string' && payload.valueKey) ||
    'value';

  if (isEmptyChart(data)) {
    return renderEmptyChartState(payload, preferredHeight);
  }

  const rows = data as Array<Record<string, unknown>>;
  const columns = chartColumns(rows);
  const requestedKeys = pickStringArray(payload, ['seriesKeys', 'keys', 'yKeys']);
  const inferredKeys = columns.filter((key) => key !== xKey && key !== 'id' && rows.some((row) => isNumeric(row[key])));
  const seriesKeys = (requestedKeys.length ? requestedKeys : inferredKeys).filter((key) => columns.includes(key));
  const distinctLabels = new Set(
    rows
      .map((row) => row[xKey])
      .filter(
        (value): value is string | number | boolean =>
          (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') &&
          String(value).trim() !== '',
      )
      .map((value) => String(value)),
  ).size;
  const hasNumericSeries = seriesKeys.some((key) => rows.some((row) => isNumeric(row[key])));
  // A KPI row or malformed chart payload should never produce an axis frame.
  // The surrounding message keeps the source table visible when there is one.
  if (distinctLabels < 2 || !hasNumericSeries) {
    return renderEmptyChartState(payload, preferredHeight);
  }
  const rawChartType =
    pickString(payload, ['chartType', 'chart', 'variant', 'kind', 'subtype']).toLowerCase() ||
    (typeof payload.type === 'string' ? payload.type.toLowerCase() : '');
  const type = lensCardTypeForChart({ ...payload, chartType: rawChartType });
  const pieNameKey = (typeof payload.nameKey === 'string' && payload.nameKey) || xKey;
  const barKeys = pickStringArray(payload, ['barKeys']);
  const lineKeys = pickStringArray(payload, ['lineKeys']);
  const areaKeys = pickStringArray(payload, ['areaKeys']);
  const selectedKeys =
    type === 'area' && areaKeys.length
      ? areaKeys
      : type === 'combo'
        ? [
            ...new Set([
              ...(barKeys.length ? barKeys : seriesKeys.slice(0, 1)),
              ...(lineKeys.length ? lineKeys : seriesKeys.slice(1, 2)),
            ]),
          ]
        : seriesKeys.length
          ? seriesKeys
          : [yKey];
  const marks =
    type === 'combo'
      ? Object.fromEntries([
          ...(barKeys.length ? barKeys : selectedKeys.slice(0, 1)).map((key) => [key, 'bar'] as const),
          ...(lineKeys.length ? lineKeys : selectedKeys.slice(1)).map((key) => [key, 'line'] as const),
        ])
      : undefined;
  const config = threadChartConfig(payload, rows, xKey, selectedKeys, type, marks);
  const chartTheme = threadEChartsTheme(theme, config);
  const height = preferredHeight ?? (type === 'pie' ? PIE_CHART_HEIGHT : CHART_HEIGHT);
  const title = pickString(payload, ['title']) || 'Answer chart';

  if (type === 'pie') {
    const pie = buildPieModel(rows, columns, { x: pieNameKey, value: yKey });
    if (!pie) {
      return renderEmptyChartState(payload, preferredHeight);
    }
    return renderChartContainer(
      payload,
      renderThreadEChart(buildLensEChartsPieOption({ pie, theme: chartTheme, config }), height, `${title}. Pie chart.`),
    );
  }

  if (type === 'scatter') {
    const numeric = columns.filter((key) => rows.some((row) => isNumeric(row[key])));
    const scatterXKey = numeric.includes(xKey) ? xKey : numeric[0];
    const scatterYKey =
      numeric.includes(yKey) && yKey !== scatterXKey ? yKey : numeric.find((key) => key !== scatterXKey);
    if (scatterXKey && scatterYKey) {
      const scatter = {
        xKey: scatterXKey,
        yKey: scatterYKey,
        groups: [{ name: formatChartLabel(scatterYKey), rows }],
      };
      return renderChartContainer(
        payload,
        renderThreadEChart(
          buildLensEChartsScatterOption({ scatter, theme: chartTheme, config }),
          height,
          `${title}. Scatter plot.`,
        ),
      );
    }
  }

  const model = buildChartModel(rows, columns, config);
  if (!model) {
    return renderEmptyChartState(payload, preferredHeight);
  }
  const timeAxis = type === 'line' || type === 'area' ? prepareLensTimeAxis(model.rows, model.xKey) : null;
  const option = buildLensEChartsOption({
    model,
    type,
    config,
    theme: chartTheme,
    timeAxis,
    bodyHeight: height,
    zoom: model.rows.length > 60,
  });

  return renderChartContainer(payload, renderThreadEChart(option, height, `${title}. ${type} chart.`));
}

/** A KPI tile after parsing: every field resolved to something the renderer can drop straight in. */
interface ParsedKpiTile {
  label: string;
  value: string;
  delta: string;
  hasDelta: boolean;
  direction: KpiDeltaDirection;
  sentiment: KpiDeltaSentiment;
  caption: string;
}

/** Screen-reader-only text: kept in the a11y tree but out of the visual layout. */
const SR_ONLY_SX = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;

function firstArrayField(payload: Record<string, unknown>, keys: Array<string>): Array<unknown> {
  for (const key of keys) {
    const value = payload[key];
    if (Array.isArray(value)) {
      return value;
    }
  }
  return [];
}

/** A tile value/delta may arrive as a finite number or a non-empty string; anything else is absent. */
function pickNumberOrString(record: Record<string, unknown>, keys: Array<string>): number | string | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return undefined;
}

/** Format a numeric tile value, reusing the block's currency/compact/percent formatters when a hint exists. */
function formatKpiNumber(value: number, format: string, currencyCode: string): string {
  const formatter = format ? createValueFormatter({ valueFormat: format, currencyCode }, 'value') : undefined;
  return formatter ? formatter(value) : formatLensValue(value);
}

/** Numeric deltas carry an explicit leading sign so a positive change reads as a gain at a glance. */
function formatKpiDelta(delta: number, format: string, currencyCode: string): string {
  const text = formatKpiNumber(delta, format, currencyCode);
  return delta > 0 ? `+${text}` : text;
}

function parseKpiTile(raw: unknown, payload: Record<string, unknown>): ParsedKpiTile | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const label = pickString(record, ['label', 'name', 'title']);
  const rawValue = pickNumberOrString(record, ['value', 'amount']);
  const format = pickString(record, ['format', 'valueFormat']) || pickString(payload, ['valueFormat']);
  const currencyCode = pickString(record, ['currencyCode']) || pickString(payload, ['currencyCode']) || 'AUD';

  const value =
    typeof rawValue === 'number'
      ? formatKpiNumber(rawValue, format, currencyCode)
      : typeof rawValue === 'string'
        ? rawValue
        : '';

  // A tile with neither a label nor a value has nothing to show; drop it rather than render an empty card.
  if (!label && !value) {
    return null;
  }

  const rawDelta = pickNumberOrString(record, ['delta', 'change']);
  const deltaFormat = pickString(record, ['deltaFormat']) || format;
  const delta =
    typeof rawDelta === 'number'
      ? formatKpiDelta(rawDelta, deltaFormat, currencyCode)
      : typeof rawDelta === 'string'
        ? rawDelta
        : '';

  return {
    label,
    value,
    delta,
    hasDelta: delta.trim().length > 0,
    direction: deriveKpiDeltaDirection(rawDelta, record.deltaDirection),
    sentiment: normalizeKpiSentiment(record.deltaSentiment ?? record.sentiment),
    caption: pickString(record, ['caption', 'scope', 'sublabel', 'subtitle', 'description']),
  };
}

/**
 * Parses a KPI block's tiles from `tiles`/`items`/`metrics`, tolerating missing fields, and caps the
 * row at {@link MAX_KPI_TILES}. Returns an empty array when there is nothing renderable.
 */
export function parseKpiTiles(payload: Record<string, unknown>): Array<ParsedKpiTile> {
  const rawTiles = firstArrayField(payload, ['tiles', 'items', 'metrics']);
  const tiles: Array<ParsedKpiTile> = [];
  for (const raw of rawTiles) {
    const tile = parseKpiTile(raw, payload);
    if (tile) {
      tiles.push(tile);
    }
    if (tiles.length >= MAX_KPI_TILES) {
      break;
    }
  }
  return tiles;
}

/** A block renders as KPI tiles when its type is a KPI kind, or a coerced `text` block declares one. */
function isKpiBlock(block: LeDataContentBlock): boolean {
  if (isKpiBlockKind(block.type)) {
    return true;
  }
  // The session/stream mapping buckets any unknown top-level type into `text` but keeps the original
  // payload, so a KPI block whose type it did not recognise still carries its kind here. Only rescue
  // such `text` blocks; never override an already-established renderable type (chart/table/sql/...).
  if (block.type !== 'text') {
    return false;
  }
  const kind = pickString(block.payload, ['kind', 'variant', 'subtype', 'blockType', 'block_type', 'type']);
  return isKpiBlockKind(kind);
}

function renderKpiTile(
  tile: ParsedKpiTile,
  tileBackground: string,
  tileBorderColor: string,
  key: string,
): React.ReactNode {
  const direction = KPI_DELTA_DIRECTION[tile.direction];
  return (
    <Box
      key={key}
      sx={{
        flex: '1 1 160px',
        minWidth: 160,
        p: 2,
        border: '1px solid',
        borderColor: tileBorderColor,
        borderRadius: '8px',
        bgcolor: tileBackground,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
      }}
    >
      <Typography
        component="div"
        sx={{
          fontSize: '11px',
          fontWeight: 500,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'text.secondary',
          lineHeight: 1.3,
        }}
      >
        {formatChartLabel(tile.label)}
      </Typography>
      <Typography
        component="div"
        sx={{
          fontSize: '1.875rem',
          fontWeight: 600,
          color: 'text.primary',
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1.1,
        }}
      >
        {tile.value}
      </Typography>
      {tile.hasDelta ? (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: '13px',
            fontWeight: 500,
            color: kpiDeltaColorRole(tile.sentiment),
          }}
        >
          <Box component="span" aria-hidden sx={{ lineHeight: 1 }}>
            {direction.glyph}
          </Box>
          <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {tile.delta}
          </Box>
          <Box component="span" sx={SR_ONLY_SX}>
            {direction.label}
          </Box>
        </Box>
      ) : null}
      {tile.caption ? (
        <Typography
          component="div"
          sx={{ fontSize: '12px', fontWeight: 450, color: 'text.secondary', lineHeight: 1.4 }}
        >
          {tile.caption}
        </Typography>
      ) : null}
    </Box>
  );
}

/**
 * Headline KPI stat tiles: 1 to {@link MAX_KPI_TILES} tiles in a wrapping row. Deltas are coloured by
 * their declared sentiment (not the numeric sign) with a direction arrow that stands on its own for
 * colour-blind readers. Renders nothing when there are no tiles to show.
 */
function renderKpiBlock(payload: Record<string, unknown>, theme: Theme): React.ReactNode {
  const tiles = parseKpiTiles(payload);
  if (tiles.length === 0) {
    return null;
  }
  const isDark = theme.palette.mode === 'dark';
  const tileBackground = isDark ? ELEVATED_SURFACE_DARK : theme.palette.background.paper;
  const tileBorderColor = isDark ? ELEVATED_SURFACE_DARK_RULE : theme.palette.divider;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
      {tiles.map((tile, index) => renderKpiTile(tile, tileBackground, tileBorderColor, `${tile.label}-${index}`))}
    </Box>
  );
}

function renderFileBlock(payload: Record<string, unknown>): React.ReactNode {
  const url = pickString(payload, ['url', 'href', 'downloadUrl']);
  const label = pickString(payload, ['label', 'name', 'filename']) || 'Open file';
  if (!url) {
    return (
      <Typography variant="body2" color="text.secondary">
        (Missing file URL)
      </Typography>
    );
  }
  return (
    <Link
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
    >
      {label}
      <OpenInNewIcon fontSize="inherit" />
    </Link>
  );
}

function renderErrorBlock(payload: Record<string, unknown>): React.ReactNode {
  const raw = pickString(payload, ['message', 'error', 'detail', 'body']) || 'Unknown error';
  const msg = getLeDataApiErrorMessage(raw);
  return <Alert severity="error">{msg}</Alert>;
}

export default function ContentBlockView({
  block,
  hideTableDownload = false,
  tableActions,
  stripTables = false,
  duplicateSql,
  hideInlineCodeFences = false,
  hideInlineChartFences = false,
  onKeepLiveSql,
  prominentLensSave = false,
  onSavedLens,
  chartAction,
  chartHeight,
  frameless = false,
  streaming = false,
}: {
  block: LeDataContentBlock;
  hideTableDownload?: boolean;
  tableActions?: React.ReactNode;
  stripTables?: boolean;
  /** Keeps duplicated fenced SQL out of a Lens answer when evidence owns the query. */
  duplicateSql?: string;
  /** A replacement keeps implementation code in the save flow and evidence shelf. */
  hideInlineCodeFences?: boolean;
  /** An editable proposal owns the one visible chart, so the model's chart fence is suppressed. */
  hideInlineChartFences?: boolean;
  /** Embedded-context only: called with (sqlText, blockId) when this SQL block's header "Add to
   *  LE Lens" control is clicked, routing the save into the unified shell's own flow instead of
   *  opening the LE Lens dialog. Absent everywhere else, including /ask-richard, where that
   *  control opens the real dialog. */
  onKeepLiveSql?: (sqlText: string, blockId: string) => void;
  /** Lens-scoped chats make the card-creation action a labelled SQL-toolbar button. */
  prominentLensSave?: boolean;
  /** Navigates an embedded chat back to the saved dashboard after confirmation. */
  onSavedLens?: (lens: Lens) => void;
  /** A compact primary action that belongs with the visual result, not below the answer. */
  chartAction?: React.ReactNode;
  /** Optional constrained height for a contextual chart preview, without changing its data or visual type. */
  chartHeight?: number;
  /** Lets a result live inside the settled answer surface without acquiring a second card edge. */
  frameless?: boolean;
  streaming?: boolean;
}) {
  const theme = useTheme();
  const textStreaming = streaming && block.type === 'text';
  const text = useSmoothReveal(
    pickString(block.payload, ['markdown', 'text', 'content', 'message', 'body']),
    textStreaming,
  );

  let body: React.ReactNode;
  if (isKpiBlock(block)) {
    // An absent or empty tiles array carries no headline numbers, so the block renders nothing at all
    // rather than an empty framed card.
    const kpiBody = renderKpiBlock(block.payload, theme);
    if (kpiBody === null) {
      return null;
    }
    body = kpiBody;
    return frameless ? (
      <Box sx={{ minWidth: 0 }}>{body}</Box>
    ) : (
      <Paper variant="outlined" sx={{ px: 1.5, py: 1.25, bgcolor: 'background.paper' }}>
        {body}
      </Paper>
    );
  }
  switch (block.type) {
    case 'text':
      body = renderTextBlock(
        text,
        theme,
        block.id,
        stripTables,
        duplicateSql,
        hideInlineCodeFences,
        hideInlineChartFences,
        onSavedLens,
        textStreaming,
      );
      break;
    case 'sql':
      body = renderSqlBlock(
        block.payload,
        onKeepLiveSql ? (sqlText) => onKeepLiveSql(sqlText, block.id) : undefined,
        prominentLensSave,
        onSavedLens,
      );
      break;
    case 'table':
      body = (
        <Box sx={{ width: '100%', overflow: 'auto' }}>
          {renderTableBlock(block.payload, block.id, hideTableDownload, tableActions)}
        </Box>
      );
      break;
    case 'chart':
      body = (
        <Box data-le-data-chart-id={block.id} sx={{ width: '100%' }}>
          {chartAction ? <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>{chartAction}</Box> : null}
          {renderChartBlock(block.payload, theme, chartHeight)}
        </Box>
      );
      break;
    case 'file':
      body = renderFileBlock(block.payload);
      break;
    case 'error':
      body = renderErrorBlock(block.payload);
      break;
    default:
      body = renderTextBlock(
        text,
        theme,
        block.id,
        stripTables,
        duplicateSql,
        hideInlineCodeFences,
        hideInlineChartFences,
        onSavedLens,
        textStreaming,
      );
  }

  if (body === null) {
    return null;
  }

  if (frameless) {
    return <Box sx={{ minWidth: 0 }}>{body}</Box>;
  }

  return (
    <Paper variant="outlined" sx={{ px: 1.5, py: 1.25, bgcolor: 'background.paper' }}>
      {body}
    </Paper>
  );
}
